import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { CandlestickChart, ArrowUpRight, ArrowDownRight } from "lucide-react";
import api from "@/lib/api";
import { PositionCards, OrderCards } from "./PhoneCards";

const fmtPnl = (v) => {
  if (v == null) return '$0.00';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}`;
};
const pnlCls = (v) => v > 0 ? 'ops-long' : v < 0 ? 'ops-short' : 'text-[hsl(var(--ops-text))]';

export default function LiveTrading() {
  const { autoRefresh, status } = useOutletContext();
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [posRes, ordRes] = await Promise.all([
          api.get("/ops/positions"),
          api.get("/ops/orders"),
        ]);
        setPositions(posRes.data.positions || []);
        setOrders(ordRes.data.orders || []);
      } catch (e) { /* silent */ }
      setLoading(false);
    };
    load();
    if (!autoRefresh) return;
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [autoRefresh]);

  if (loading) return (
    <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[hsl(var(--ops-surface))]" /><Skeleton className="h-64 bg-[hsl(var(--ops-surface))]" /><Skeleton className="h-48 bg-[hsl(var(--ops-surface))]" /></div>
  );

  const totalUnrealized = positions.reduce((s,p) => s + (p.unrealized_pnl || 0), 0);
  // Only claim "flat and watching" when the bot is actually up and checking in.
  const hbAge = status?.last_heartbeat ? (Date.now() - new Date(status.last_heartbeat).getTime()) / 1000 : null;
  const botWatching = status?.bot_status === "running" && hbAge !== null && hbAge < 30;

  return (
    <div data-testid="ops-trading-page" className="space-y-5">
      <div className="flex items-center justify-between max-sm:gap-3">
        <div className="max-sm:min-w-0">
          <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Live Trading</h1>
          <p className="text-xs text-[hsl(var(--ops-text-muted))]">Current positions and active orders</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ops-text-muted))]">Unrealized P&L</div>
            <div className={`text-base font-mono-num font-semibold ${pnlCls(totalUnrealized)}`}>{fmtPnl(totalUnrealized)}</div>
          </div>
        </div>
      </div>

      {/* Positions */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
        <div className="px-3 py-2 border-b border-[hsl(var(--ops-border))]">
          <span className="text-sm font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2">
            <CandlestickChart className="size-4 text-[hsl(var(--ops-brand))]" />
            Open Positions
            <Badge className="rounded text-[10px] bg-[hsl(var(--ops-surface-2))] text-[hsl(var(--ops-text-muted))] border-0">{positions.length}</Badge>
          </span>
        </div>
        {positions.length === 0 ? (
          <div data-testid="ops-no-position" className="px-3 py-8 text-center text-sm text-[hsl(var(--ops-text-muted))]">
            {botWatching ? "No open position. The bot is flat and watching the market." : "No open position."}
          </div>
        ) : (
          <>
          <PositionCards positions={positions} full testId="ops-positions-cards" />
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="ops-positions-table" className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text-muted))]">
                  <th className="text-left py-2 px-3 font-medium sticky top-0 bg-[hsl(var(--ops-surface))]">Instrument</th>
                  <th className="text-center py-2 px-3 font-medium">Direction</th>
                  <th className="text-right py-2 px-3 font-medium">Qty</th>
                  <th className="text-right py-2 px-3 font-medium">Entry</th>
                  <th className="text-right py-2 px-3 font-medium">Current</th>
                  <th className="text-right py-2 px-3 font-medium">Unrl P&L</th>
                  <th className="text-right py-2 px-3 font-medium">Stop Loss</th>
                  <th className="text-right py-2 px-3 font-medium">Target</th>
                  <th className="text-left py-2 px-3 font-medium">Setup</th>
                  <th className="text-right py-2 px-3 font-medium">Score</th>
                  <th className="text-left py-2 px-3 font-medium">Entry Time</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, i) => (
                  <tr key={i} className="border-b border-[hsl(var(--ops-border))]/50 hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-100 h-8">
                    <td className="py-1 px-3 font-medium text-[hsl(var(--ops-text))]">{p.instrument}</td>
                    <td className="py-1 px-3 text-center">
                      <Badge className={`rounded text-[10px] border-0 px-1.5 gap-0.5 ${p.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
                        {p.direction === 'LONG' ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
                        {p.direction}
                      </Badge>
                    </td>
                    <td className="py-1 px-3 text-right font-mono-num">{p.quantity}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{p.entry_price?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{p.current_price?.toFixed(2)}</td>
                    <td className={`py-1 px-3 text-right font-mono-num font-semibold ${pnlCls(p.unrealized_pnl)}`}>{fmtPnl(p.unrealized_pnl)}</td>
                    <td className="py-1 px-3 text-right font-mono-num text-[hsl(var(--ops-warn))]">{p.stop_loss?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-right font-mono-num text-[hsl(var(--ops-brand))]">{p.profit_target?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-[hsl(var(--ops-text-muted))]">{p.setup}</td>
                    <td className="py-1 px-3 text-right font-mono-num text-[hsl(var(--ops-brand))]">{p.mentor_score}</td>
                    <td className="py-1 px-3 font-mono-num text-[hsl(var(--ops-text-muted))]">{p.entry_time ? new Date(p.entry_time).toLocaleTimeString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      {/* Active Orders */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
        <div className="px-3 py-2 border-b border-[hsl(var(--ops-border))]">
          <span className="text-sm font-semibold text-[hsl(var(--ops-text))] flex items-center gap-2">
            Active Orders
            <Badge className="rounded text-[10px] bg-[hsl(var(--ops-surface-2))] text-[hsl(var(--ops-text-muted))] border-0">{orders.length}</Badge>
          </span>
        </div>
        {orders.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-[hsl(var(--ops-text-muted))]">No working orders</div>
        ) : (
          <>
          <OrderCards orders={orders} testId="ops-orders-cards" />
          <div className="hidden md:block overflow-x-auto">
            <table data-testid="ops-orders-table" className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text-muted))]">
                  <th className="text-left py-2 px-3 font-medium">Instrument</th>
                  <th className="text-left py-2 px-3 font-medium">Type</th>
                  <th className="text-center py-2 px-3 font-medium">Side</th>
                  <th className="text-right py-2 px-3 font-medium">Qty</th>
                  <th className="text-right py-2 px-3 font-medium">Price</th>
                  <th className="text-left py-2 px-3 font-medium">Label</th>
                  <th className="text-center py-2 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr key={i} className="border-b border-[hsl(var(--ops-border))]/50 hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-100 h-8">
                    <td className="py-1 px-3 font-medium text-[hsl(var(--ops-text))]">{o.instrument}</td>
                    <td className="py-1 px-3 text-[hsl(var(--ops-text-muted))]">{o.type}</td>
                    <td className="py-1 px-3 text-center">
                      <Badge className={`rounded text-[10px] border-0 px-1.5 ${o.direction === 'BUY' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>{o.direction}</Badge>
                    </td>
                    <td className="py-1 px-3 text-right font-mono-num">{o.quantity}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{o.price?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-[hsl(var(--ops-text-muted))]">{o.label || '—'}</td>
                    <td className="py-1 px-3 text-center">
                      <Badge className="rounded text-[10px] bg-[hsl(var(--ops-warn))]/15 text-[hsl(var(--ops-warn))] border-0">{o.status?.toUpperCase()}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>
    </div>
  );
}
